import { POST } from "../../API/API";

const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 10_000;

const buildDictationConfiguration = ({
  language,
  ttsEnabled,
  sttEnabled = false,
  translationsEnabled = true,
}) => {
  return {
    translations_enabled: Boolean(translationsEnabled),
    stt_enabled: Boolean(sttEnabled),
    tts_enabled: Boolean(ttsEnabled),
    language,
  };
};

const createDictationSession = async ({
  language,
  ttsEnabled,
  sttEnabled,
  translationsEnabled,
}) => {
  const configuration = buildDictationConfiguration({
    language,
    ttsEnabled,
    sttEnabled,
    translationsEnabled,
  });

  return new Promise((resolve, reject) => {
    POST(
      "/api/v1/connector/realtime/dictation",
      { configuration },
      (response) => {
        resolve(response?.data);
      },
      (error) => {
        reject(error);
      }
    );
  });
};

export default class RealtimeApiClient {
  constructor({
    onOpen,
    onClose,
    onError,
    onTranslatedText,
    onRecognizedText,
    onConfigurationUpdated,
    onAudio,
  } = {}) {
    this.ws = null;
    this.isConnecting = false;

    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.onTranslatedText = onTranslatedText;
    this.onRecognizedText = onRecognizedText;
    this.onConfigurationUpdated = onConfigurationUpdated;
    this.onAudio = onAudio;

    this.backoffAttempt = 0;
    this.reconnectTimer = null;

    this.desiredLanguage = null;
    this.desiredTtsEnabled = false;
    this.desiredSttEnabled = false;
    this.desiredTranslationsEnabled = true;
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  async connect({ language, ttsEnabled, sttEnabled, translationsEnabled } = {}) {
    if (!language) {
      throw new Error("RealtimeApiClient.connect: language is required");
    }

    this.desiredLanguage = language;
    this.desiredTtsEnabled = Boolean(ttsEnabled);
    this.desiredSttEnabled = Boolean(sttEnabled);
    this.desiredTranslationsEnabled =
      translationsEnabled === undefined ? true : Boolean(translationsEnabled);

    if (this.isConnecting || this.isConnected) {
      return;
    }

    this.isConnecting = true;
    this.clearReconnectTimer();

    try {
      const session = await createDictationSession({
        language: this.desiredLanguage,
        ttsEnabled: this.desiredTtsEnabled,
        sttEnabled: this.desiredSttEnabled,
        translationsEnabled: this.desiredTranslationsEnabled,
      });

      const accessUrl = session?.access_url;
      if (!accessUrl) {
        throw new Error("RealtimeApiClient.connect: missing access_url");
      }

      await this.connectWebSocket(accessUrl);
      this.backoffAttempt = 0;
    } finally {
      this.isConnecting = false;
    }
  }

  async reconnect() {
    await this.disconnect();
    return this.connect({
      language: this.desiredLanguage,
      ttsEnabled: this.desiredTtsEnabled,
      sttEnabled: this.desiredSttEnabled,
      translationsEnabled: this.desiredTranslationsEnabled,
    });
  }

  scheduleReconnect({ baseMs = DEFAULT_BACKOFF_BASE_MS, maxMs = DEFAULT_BACKOFF_MAX_MS } = {}) {
    if (!this.desiredLanguage) {
      return;
    }

    this.clearReconnectTimer();

    const delay = Math.min(maxMs, baseMs * 2 ** this.backoffAttempt);
    this.backoffAttempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnect().catch((err) => {
        if (this.onError) {
          this.onError(err);
        }
        this.scheduleReconnect({ baseMs, maxMs });
      });
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  async disconnect() {
    this.clearReconnectTimer();

    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      } catch {
        // noop
      }
      this.ws = null;
    }

    this.isConnecting = false;
  }

  async connectWebSocket(accessUrl) {
    await this.disconnect();

    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(accessUrl);
        ws.binaryType = "arraybuffer";
        this.ws = ws;

        ws.onopen = () => {
          if (this.onOpen) {
            this.onOpen();
          }

          // Ensure runtime config matches latest desired state.
          this.updateConfiguration({
            language: this.desiredLanguage,
            ttsEnabled: this.desiredTtsEnabled,
            sttEnabled: this.desiredSttEnabled,
            translationsEnabled: this.desiredTranslationsEnabled,
          }).catch(() => {
            // Silent for MVP
          });

          resolve();
        };

        ws.onerror = (event) => {
          const err = event?.error || new Error("RealtimeApiClient websocket error");
          if (this.onError) {
            this.onError(err);
          }
          reject(err);
        };

        ws.onclose = () => {
          if (this.onClose) {
            this.onClose();
          }
        };

        ws.onmessage = (event) => {
          const data = event?.data;

          if (typeof data === "string") {
            this.handleJsonMessage(data);
            return;
          }

          if (data instanceof ArrayBuffer) {
            if (this.onAudio) {
              this.onAudio(data);
            }
            return;
          }

          if (data instanceof Blob) {
            data
              .arrayBuffer()
              .then((buf) => {
                if (this.onAudio) {
                  this.onAudio(buf);
                }
              })
              .catch(() => {
                // noop
              });
          }
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  handleJsonMessage(raw) {
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    if (parsed?.type === "text_translated") {
      if (this.onTranslatedText) {
        this.onTranslatedText(parsed);
      }
      return;
    }

    if (parsed?.type === "text_recognized") {
      if (this.onRecognizedText) {
        this.onRecognizedText(parsed);
      }
      return;
    }

    if (parsed?.type === "configuration_updated") {
      if (this.onConfigurationUpdated) {
        this.onConfigurationUpdated(parsed);
      }
      return;
    }

    if (parsed?.type === "error") {
      if (this.onError) {
        this.onError(new Error(parsed?.message || parsed?.code || "Realtime API error"));
      }
    }
  }

  sendJson(payload) {
    if (!this.isConnected) {
      throw new Error("RealtimeApiClient: websocket not connected");
    }

    this.ws.send(JSON.stringify(payload));
  }

  sendTranslateText(text) {
    if (!text) {
      return;
    }

    this.sendJson({
      type: "translate_text",
      text,
    });
  }

  sendAudio(arrayBuffer) {
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      return;
    }

    if (!this.isConnected) {
      throw new Error("RealtimeApiClient: websocket not connected");
    }

    this.ws.send(arrayBuffer);
  }

  async updateConfiguration({
    language,
    ttsEnabled,
    sttEnabled = this.desiredSttEnabled,
    translationsEnabled = this.desiredTranslationsEnabled,
  } = {}) {
    if (!language) {
      return;
    }

    this.desiredLanguage = language;
    this.desiredTtsEnabled = Boolean(ttsEnabled);
    this.desiredSttEnabled = Boolean(sttEnabled);
    this.desiredTranslationsEnabled =
      translationsEnabled === undefined ? true : Boolean(translationsEnabled);

    if (!this.isConnected) {
      return;
    }

    const configuration = buildDictationConfiguration({
      language: this.desiredLanguage,
      ttsEnabled: this.desiredTtsEnabled,
      sttEnabled: this.desiredSttEnabled,
      translationsEnabled: this.desiredTranslationsEnabled,
    });

    this.sendJson({
      type: "update_configuration",
      configuration,
    });
  }
}
